<template>
  <section
    v-if="rows.length || pending || error"
    class="gr-actdocs"
    aria-labelledby="gr-actdocs-heading"
  >
    <h2 id="gr-actdocs-heading" class="gr-actdocs__heading">
      Dokumentasi kegiatan
    </h2>

    <p v-if="pending" class="gr-actdocs__loading" aria-live="polite">
      Memuat dokumentasi kegiatan…
    </p>

    <p v-else-if="error" class="gr-actdocs__error" role="alert">
      <span>Dokumentasi kegiatan gagal dimuat.</span>
      <button
        type="button"
        class="gr-btn gr-btn--quiet"
        @click="() => refresh()"
      >
        Coba lagi
      </button>
    </p>

    <div v-else class="gr-actdocs__grid">
      <button
        v-for="row in rows"
        :key="row.uuid"
        type="button"
        class="gr-actdocs__card"
        @click="openDocumentation(row)"
      >
        <div class="gr-actdocs__media">
          <img
            v-if="row.photos[0]?.imageUrl && !failed.has(row.uuid)"
            :src="row.photos[0].imageUrl"
            :alt="row.photos[0].altText || ''"
            class="gr-actdocs__img"
            loading="lazy"
            @error="markFailed(row.uuid)"
          />
          <div v-else class="gr-actdocs__img gr-actdocs__fallback">
            <v-icon icon="mdi-image-off-outline" size="24" aria-hidden="true" />
            <span class="gr-actdocs__sr">Gambar tidak tersedia</span>
          </div>
        </div>
        <div class="gr-actdocs__copy">
          <h3 class="gr-actdocs__title">{{ row.title }}</h3>
          <p class="gr-actdocs__text">{{ row.description }}</p>
        </div>
      </button>
    </div>

    <DocumentationDialog
      :open="isDialogOpen"
      :uuid="activeUuid"
      :summary="activeSummary"
      @close="closeDocumentation"
    />
  </section>
</template>

<script lang="ts" setup>
import DocumentationDialog from "@/features/documentation/views/components/DocumentationDialog.vue";
import { usePublicDocumentations } from "~/features/documentation/usePublicDocumentations";
import type {
  DocumentationFilters,
  PublicDocumentation,
} from "~/features/documentation/documentation";

const props = defineProps<{ slug: string }>();

const filters = ref<DocumentationFilters>({
  page: 1,
  size: 20,
  activitySlug: props.slug,
});

const { rows, pending, error, refresh } = usePublicDocumentations(filters);

const failed = ref(new Set<string>());

// The candidate has no documentation detail route: a record opens in place.
const isDialogOpen = ref(false);
const activeUuid = ref<string | null>(null);
const activeSummary = ref<PublicDocumentation | null>(null);

// The dialog takes a one-way `:open` prop plus a `@close` event rather than
// `v-model`, so Vuetify never sees the binding it uses to restore focus and
// drops it on `<body>` when the dialog closes. A keyboard user who pressed
// Escape would then have to tab from the top of the document to get back to
// what they opened (WCAG 2.4.3). Hold the trigger ourselves.
let dialogTrigger: HTMLElement | null = null;

function captureDialogTrigger(): void {
  dialogTrigger =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
}

function openDocumentation(row: PublicDocumentation): void {
  captureDialogTrigger();
  activeUuid.value = row.uuid;
  activeSummary.value = row;
  isDialogOpen.value = true;
}

function closeDocumentation(): void {
  isDialogOpen.value = false;
  const trigger = dialogTrigger;
  dialogTrigger = null;
  if (trigger) void nextTick(() => trigger.focus({ preventScroll: true }));
}
const markFailed = (uuid: string): void => {
  failed.value = new Set(failed.value).add(uuid);
};
</script>

<style scoped>
.gr-actdocs {
  margin-top: clamp(2rem, 1.5rem + 2vw, 3rem);
  padding-top: 20px;
  border-top: 1px solid var(--gr-line-strong);
}

.gr-actdocs__heading {
  margin: 0 0 1.25rem;
  color: var(--gr-ink);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
}

.gr-actdocs__loading {
  margin: 0;
  color: var(--gr-ink-3);
  font-size: 0.875rem;
}

.gr-actdocs__error {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin: 0;
  padding: 12px;
  border: 1px solid var(--gr-danger);
  border-radius: 4px;
  background-color: rgba(179, 38, 30, 0.06);
  color: var(--gr-danger);
  font-size: 0.875rem;
}

.gr-actdocs__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
  gap: 16px;
}

button.gr-actdocs__card {
  padding: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.gr-actdocs__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--gr-line);
  border-radius: 4px;
  background-color: var(--gr-surface);
  color: inherit;
  text-decoration: none;
}

@media (hover: hover) {
  .gr-actdocs__card:hover {
    border-color: var(--gr-green-deep);
  }
}

.gr-actdocs__media {
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  background-color: var(--gr-green-tint);
}

.gr-actdocs__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gr-actdocs__fallback {
  display: grid;
  place-items: center;
  color: var(--gr-ink-3);
}

.gr-actdocs__copy {
  padding: 14px;
}

.gr-actdocs__title {
  margin: 0 0 6px;
  color: var(--gr-ink);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.gr-actdocs__text {
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 0.875rem;
  line-height: 1.6;
}

.gr-actdocs__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
</style>
